import classNames from 'classnames';
import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CurrencyIcon, CurrencyEnum } from '@spectrocoin/sc-currencies';
import TextNode, { TextStyles } from '../TextNode/TextNode';
import TestIds, { formatTestId } from '../../tests/TestIds';

import styles from './DonationHeader.module.scss';

const messages = defineMessages({
	description: {
		id: 'payments.description',
		defaultMessage: 'Description',
	},
	id: {
		id: 'payments.id',
		defaultMessage: 'ID',
	},
	donation: {
		id: 'payments.donation',
		defaultMessage: 'Donation',
	},
	cryptocurrency: {
		id: 'payments.cryptocurrency',
		defaultMessage: 'Cryptocurrency',
	},
	labeledValue: {
		id: 'base.label_value',
		defaultMessage: '{Label}: {Value}',
	},
});

type DonationHeaderProps = {
	id: string;
	description?: string | null;
	currencyInfo?: {
		currencyCode: CurrencyEnum;
		currencyName: string;
	};
};

const DonationHeader: FC<DonationHeaderProps> = ({ id, description, currencyInfo }) => {
	const { formatMessage } = useIntl();

	return (
		<div data-cy={TestIds.DonationHeader} className={classNames(styles.header, styles.border)}>
			<div className={classNames(styles.block, styles.block100, styles.donation)}>
				<label>{formatMessage(messages.donation)}</label>
			</div>
			<div className={classNames(styles.block, styles.block100, styles.vertical)}>
				<div className={styles.details}>
					<div>
						<TextNode data-cy={TestIds.DonationHeaderId} style={TextStyles.BreakWord}>
							{formatMessage(messages.labeledValue, {
								Label: formatMessage(messages.id),
								Value: id,
							})}
						</TextNode>
					</div>
					{description && (
						<div>
							<TextNode
								data-cy={TestIds.DonationHeaderDescription}
								style={TextStyles.BreakWord}
							>
								{formatMessage(messages.labeledValue, {
									Label: formatMessage(messages.description),
									Value: description,
								})}
							</TextNode>
						</div>
					)}
				</div>
			</div>

			{currencyInfo && (
				<div
					data-cy={TestIds.DonationHeaderCurrencyInfo}
					className={classNames(styles.currencyInfo, styles.block, styles.block100)}
				>
					<div className={classNames(styles.block, styles.block50)}>
						{formatMessage(messages.cryptocurrency)}
					</div>
					<div
						className={classNames(
							styles.currency,
							styles.block,
							styles.block50,
							styles.right
						)}
					>
						<CurrencyIcon
							className={styles.icon}
							currencyType={currencyInfo.currencyCode}
						/>
						<div
							data-cy={formatTestId(
								TestIds.DonationHeaderCurrencyInfoCurrency_0,
								currencyInfo.currencyCode
							)}
						>{`${currencyInfo.currencyName} ${currencyInfo.currencyCode}`}</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DonationHeader;
